import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { SupplierPortalRisk } from '../models/settings.model';
import { RiskLayer } from '../models/map.model';

const AGD_GEOSPATIAL_COMPUTE_SOURCE = 'agd-geospatial';

// A risk's `name` can lag its compute version (a "v2" catalog entry may still
// be named `..._v1`), so agd-geospatial tiles are requested by `riskFunction`.
// Other compute sources keep `name`: their `riskFunction` is a BigQuery UDF
// name the tile service does not recognise.
export function getTileRiskId(risk: SupplierPortalRisk): string {
  return risk.computeSource === AGD_GEOSPATIAL_COMPUTE_SOURCE
    ? risk.riskFunction?.trim() || risk.name
    : risk.name;
}

// The tile cache is page-wide and filled by whichever caller asks first, so
// every caller must request the same ids.
export function tileRiskIds(risks: SupplierPortalRisk[]): string[] {
  return risks.filter((risk) => risk.type === 'overlap').map(getTileRiskId);
}

export function toRiskLayers(
  risks: SupplierPortalRisk[],
  results: TileUrlResult[]
): RiskLayer[] {
  return results.map((result) => {
    const settingsRisk = risks.find(
      (risk) => getTileRiskId(risk) === result.risk_id
    );
    return {
      id: result.risk_id,
      name: settingsRisk?.display || result.risk_id,
      dataurl: result.xyz_url,
      type: 'raster',
      checked: false,
      color: result.color,
      opacity: result.opacity
    };
  });
}

export interface TileUrlsRequest {
  risk_ids: string[];
}

export interface TileUrlResult {
  risk_id: string;
  xyz_url: string;
  color: string;
  opacity: number;
}

export interface TileUrlsResponse {
  results: TileUrlResult[];
}

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private readonly http = inject(HttpClient);
  private cachedResults: TileUrlResult[] | null = null;

  getTileUrls(riskIds: string[]): Observable<TileUrlsResponse> {
    if (this.cachedResults) {
      return of({ results: this.cachedResults });
    }
    return this.fetchTileUrls(riskIds).pipe(
      tap((resp) => (this.cachedResults = resp.results))
    );
  }

  refreshTileUrls(riskIds: string[]): Observable<TileUrlsResponse> {
    return this.fetchTileUrls(riskIds).pipe(
      tap((resp) => {
        const updated = new Map(
          (this.cachedResults ?? []).map((r) => [r.risk_id, r])
        );
        for (const result of resp.results) {
          updated.set(result.risk_id, result);
        }
        this.cachedResults = Array.from(updated.values());
      })
    );
  }

  private fetchTileUrls(riskIds: string[]): Observable<TileUrlsResponse> {
    const url = `${environment.CRD_API}v1/supplier-portal/layers/tile-urls`;
    const payload: TileUrlsRequest = { risk_ids: riskIds };
    return this.http.post<TileUrlsResponse>(url, payload);
  }
}
